/**
 * 智能体操作日志管理器
 * @author Joey
 */
import { useToast } from "@/hooks/use-toast";
import { canUseWindow } from "@/utils/canuseWindow";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserManagementHooks } from "./agent-dashboard-user-management-manager";
import { AgentOperationLogData, agentOperationLogsList, AgentOperationLogsListRequest } from "@/services/agent-operation-logs-list";

export interface SearchFormData {
  page?: number;
  pageSize?: number;
  filterType?: string;
  filterKeyword?: string;
  moduleType?: string;
  startDate?: string;
  endDate?: string;
  total?: number;
}

type OperationLogsChangeEventDetail = {
  operationLogs: AgentOperationLogData[];
};
type OperationLogsChangeEvent = CustomEvent<OperationLogsChangeEventDetail>;

type SearchFormChangeEventDetail = SearchFormData;
type SearchFormChangeEvent = CustomEvent<SearchFormChangeEventDetail>;

class AgentOperationLogApi {
  constructor() { }
  // 请求操作日志列表
  async fetchOperationLogsList(params: AgentOperationLogsListRequest) {
    return await agentOperationLogsList(params);
  }
}

export class AgentOperationLogManager {
  private static instance: AgentOperationLogManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_OPERATION_LOG_LIST";
  private api: AgentOperationLogApi;
  // 当前智能体ID
  private agentId: string = "";
  // 智能体操作日志列表
  private operationLogs: AgentOperationLogData[] = [];
  // 搜索表单的数据
  private searchForm: SearchFormData = {
    page: 1,
    pageSize: 20,
    filterType: '',
    filterKeyword: '',
    moduleType: '',
    startDate: '',
    endDate: '',
    total: 0,
  }

  private constructor() {
    this.api = new AgentOperationLogApi();
  }

  // 单例模式
  public static getInstance(): AgentOperationLogManager {
    if (!AgentOperationLogManager.instance) {
      AgentOperationLogManager.instance = new AgentOperationLogManager();
    }
    return AgentOperationLogManager.instance;
  }

  // 初始化
  public async init(agentId: string) {
    this.setAgentId(agentId);
    try {
      // 日志每次都请求最新数据
      await this.fetchOperationLogsList();
    } catch (error) {
      console.error("Failed to init agent operation logs cache:", error);
      throw error;
    }
  }

  // 请求列表数据
  public async fetchOperationLogsList(page?: number) {
    try {
      const data = await this.api.fetchOperationLogsList({
        ...this.getSearchForm(),
        agentId: this.agentId,
      });
      this.setOperationLogs(data.list);
      this.setSearchForm({
        ...this.searchForm,
        page: page || data.page,
        pageSize: data.pageSize,
        total: data.total,
      });
    } catch (error) {
      console.error("Failed to fetch agent operation logs list:", error);
      this.dispatchOperationLogsChange();
      throw error;
    }
  }

  // 重置列表和搜索表单
  public resetOperationLogsAndSearchForm() {
    this.setOperationLogs([]);
    this.setSearchForm({
      page: 1,
      pageSize: 20,
      filterType: '',
      filterKeyword: '',
      moduleType: '',
      startDate: '',
      endDate: '',
    });
  }

  // ==================== 读取写入操作 ====================
  public getOperationLogs() {
    return this.operationLogs;
  }
  private setOperationLogs(operationLogs: AgentOperationLogData[]) {
    this.operationLogs = operationLogs;
    this.dispatchOperationLogsChange();
  }

  public getAgentId() {
    return this.agentId;
  }
  public setAgentId(agentId: string) {
    this.agentId = agentId;
  }

  public getSearchForm() {
    return this.searchForm;
  }
  public setSearchForm(form: SearchFormData) {
    this.searchForm = form;
    this.dispatchSearchFormChange();
  }

  // ==================== 订阅通知操作 ====================
  // 监听列表变化事件
  private dispatchOperationLogsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<OperationLogsChangeEventDetail>(`${this.STORAGE_KEY}:operation-logs-change`, {
      detail: { operationLogs: this.operationLogs }
    });
    window.dispatchEvent(event);
  }
  public onOperationLogsChange(listener: (e: OperationLogsChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:operation-logs-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:operation-logs-change`, listener);
  }

  // 监听搜索表单变化事件
  private dispatchSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<SearchFormChangeEventDetail>(`${this.STORAGE_KEY}:search-form-change`, {
      detail: this.searchForm
    });
    window.dispatchEvent(event);
  }
  public onSearchFormChange(listener: (e: SearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:search-form-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:search-form-change`, listener);
  }
}

// ============= Hooks =============
// 智能体操作日志列表数据
const useAgentOperationLogListData = () => {
  const toast = useToast();
  const { id } = useParams();

  const [operationLogs, setOperationLogs] = useState(agentOperationLogManager.getOperationLogs());
  const [searchFormData, setSearchFormData] = useState(agentOperationLogManager.getSearchForm());

  const [loading, setLoading] = useState(false);

  useUserManagementHooks.useListenerAfterAction('operation-log');

  const init = async () => {
    if (!id) {
      console.error('Agent id is required');
      return;
    }
    agentOperationLogManager.setAgentId(id);
    try {
      setLoading(true);
      await agentOperationLogManager.init(id);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const search = async (page?: number) => {
    setLoading(true);
    try {
      await agentOperationLogManager.fetchOperationLogsList(page);
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  const changeSearchForm = (key: keyof SearchFormData, value: string | number) => {
    if (key === 'startDate' || key === 'endDate') {
      agentOperationLogManager.setSearchForm({
        ...searchFormData,
        startDate: value[0],
        endDate: value[1],
      });
      return;
    }
    agentOperationLogManager.setSearchForm({
      ...searchFormData,
      [key]: value,
    });
  }

  const changePage = (page: number) => {
    changeSearchForm('page', page);
  }

  const resetAndSearch = async () => {
    agentOperationLogManager.setSearchForm({
      filterType: "",
      filterKeyword: "",
      moduleType: "",
      startDate: "",
      endDate: "",
      page: 1,
      pageSize: 20,
    });
    await search();
  }

  useEffect(() => {
    init();
  }, [id])

  useEffect(() => {
    const unsubscribe = agentOperationLogManager.onOperationLogsChange((e) => {
      setOperationLogs(e.detail.operationLogs)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentOperationLogManager.onSearchFormChange((e) => {
      setSearchFormData(e.detail)
    })
    return unsubscribe
  }, [])


  return {
    operationLogs,
    searchFormData,
    loading,
    search,
    changePage,
    resetAndSearch,
    changeSearchForm,
  }
}


export const agentOperationLogHooks = {
  useAgentOperationLogListData,
}

// 导出单例实例
export const agentOperationLogManager = AgentOperationLogManager.getInstance();
